/** @format */

import {useState, useRef, useEffect, useCallback} from 'react';

import {Alert, Box, CircularProgress, LinearProgress, TextField, Typography} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {Trans, useTranslation} from 'react-i18next';

import type {AiAgentChatMessageRole, AiAgentChatMessageStatus} from 'graphql/generated/types';

import Snackbar from 'components/Snackbar';

import MessageBubble from '../MessageBubble';

import {
  ChatAreaRoot,
  MessagesContainer,
  PlaceholderContainer,
  InputContainer,
  ThinkingContainer,
  SendButton,
} from './styles';
import {useSpinnerPhrase} from './useSpinnerPhrase';

interface Message {
  readonly id: string;
  readonly role: AiAgentChatMessageRole;
  readonly content: string;
  readonly status: AiAgentChatMessageStatus;
}

interface ChatAreaProps {
  chatId: string | null;
  messages: readonly Message[];
  loading: boolean;
  error: string | null;
  sendError: string | null;
  isPending: boolean;
  pollTimedOut: boolean;
  onSendMessage: (message: string) => Promise<boolean>;
  onClearSendError: () => void;
}

const ChatArea = ({
  chatId,
  messages,
  loading,
  error,
  sendError,
  isPending,
  pollTimedOut,
  onSendMessage,
  onClearSendError,
}: ChatAreaProps) => {
  const {t} = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const thinking = isPending || submitting;
  const inputDisabled = thinking || loading;
  const phrase = useSpinnerPhrase(thinking);

  // Ключуем на стабильную сигнатуру последнего сообщения (count + id + status),
  // а не на сам массив messages: при поллинге (no-cache) каждый tick создаёт
  // новый array-ref с идентичным содержимым, и эффект на [messages, thinking]
  // срабатывал на каждый ~2.5с poll, выдёргивая пользователя вниз при чтении
  // истории выше. Сигнатура меняется только когда сообщения реально меняются.
  const lastMessage = messages[messages.length - 1];
  const scrollSignature = lastMessage
    ? `${messages.length}:${lastMessage.id}:${lastMessage.status}`
    : `${messages.length}`;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [scrollSignature, thinking]);

  const handleSubmit = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || inputDisabled) return;

    setSubmitting(true);
    try {
      const ok = await onSendMessage(trimmed);
      if (ok) setInputValue('');
    } finally {
      // onSendMessage (hook) по контракту не бросает, но не завязываем на это
      // разблокировку ввода: finally гарантирует сброс submitting в любом случае.
      setSubmitting(false);
    }
  }, [inputValue, inputDisabled, onSendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const inputBlock = (
    <InputContainer>
      <TextField
        fullWidth
        multiline
        maxRows={4}
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('Введите сообщение...')}
        size='small'
        disabled={inputDisabled}
      />
      <SendButton
        onClick={handleSubmit}
        disabled={!inputValue.trim() || inputDisabled}
        aria-label={t('Отправить сообщение')}
      >
        <SendIcon />
      </SendButton>
    </InputContainer>
  );

  const thinkingIndicator = thinking && phrase && (
    <ThinkingContainer>
      <CircularProgress size={16} />
      <Typography variant='body2' data-testid='thinking-phrase'>
        {phrase}...
      </Typography>
    </ThinkingContainer>
  );

  // pollTimedOut-нотис и sendError-Snackbar не зависят от chatId — рендерим один
  // раз и переиспользуем в обеих ветках (пустой стейт и активный чат).
  const noticesBlock = (
    <>
      {pollTimedOut && (
        <Alert severity='warning'>
          <Trans>Ответ занимает слишком долго. Обновите страницу.</Trans>
        </Alert>
      )}
      <Snackbar
        isSnackbarOpen={sendError !== null}
        handleSnackbarClose={onClearSendError}
        message={sendError ?? ''}
        vertical='bottom'
        horizontal='left'
      />
    </>
  );

  if (!chatId) {
    return (
      <ChatAreaRoot>
        {!thinking ? (
          <PlaceholderContainer>
            <Typography color='textSecondary'>
              <Trans>Выберите чат или создайте новый</Trans>
            </Typography>
          </PlaceholderContainer>
        ) : (
          <MessagesContainer>
            {thinkingIndicator}
            <Box ref={messagesEndRef} />
          </MessagesContainer>
        )}
        {noticesBlock}
        {inputBlock}
      </ChatAreaRoot>
    );
  }

  return (
    <ChatAreaRoot>
      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}
      {noticesBlock}

      <MessagesContainer>
        {messages
          .filter(msg => !(msg.role === 'ASSISTANT' && msg.status === 'PENDING'))
          .map(msg => (
            <MessageBubble key={msg.id} role={msg.role} content={msg.content} status={msg.status} />
          ))}
        {thinkingIndicator}
        <Box ref={messagesEndRef} />
      </MessagesContainer>

      {inputBlock}
    </ChatAreaRoot>
  );
};

export default ChatArea;
